import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import { formatClientLogEntry, formatShortDateTime } from '../../../../utils/clientLogFormatters';

const ACTIVITY_TYPE_CONFIG = {
  create: { label: 'Created', icon: BusinessOutlinedIcon, color: 'success' },
  update: { label: 'Updated', icon: BusinessOutlinedIcon, color: 'primary' },
  status: { label: 'Status', icon: ToggleOnOutlinedIcon, color: 'warning' },
  invoice: { label: 'Invoice pattern', icon: ReceiptLongOutlinedIcon, color: 'info' },
  admins: { label: 'Admins', icon: GroupOutlinedIcon, color: 'secondary' },
  request: { label: 'Request', icon: HowToRegOutlinedIcon, color: 'success' },
  poc: { label: 'POC', icon: ContactMailOutlinedIcon, color: 'default' },
  other: { label: 'Other', icon: HistoryOutlinedIcon, color: 'default' },
};

const getActivityType = (log) => {
  if (!log?.action || typeof log.action !== 'string') return 'other';
  if (log.action === 'client.created') return 'create';
  if (log.action === 'client.updated') return 'update';
  if (log.action === 'client.status_updated') return 'status';
  if (log.action === 'client.invoice_pattern_updated') return 'invoice';
  if (log.action === 'client.admins_updated') return 'admins';
  if (log.action === 'client.request_accepted') return 'request';
  if (log.action.startsWith('poc.')) return 'poc';
  return 'other';
};

const toStartOfDay = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const toEndOfDay = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const getActorFilterValue = (log) => log.actor_user_id || log.actor_email || 'system';
const getActorFilterLabel = (log) => log.actor_name || log.actor_email || 'System';
const normalizeMultiSelectValue = (value) => (Array.isArray(value) ? value : [value]);

const ActivityTab = ({
  logs = [],
  loadingLogs = false,
  error = '',
}) => {
  const theme = useTheme();
  const filterButtonColor = theme.palette.mode === 'dark' ? '#fff' : '#000';
  const toolbarButtonSx = {
    textTransform: 'none',
    minHeight: 40,
    borderRadius: 1,
    borderColor: theme.palette.divider,
    color: filterButtonColor,
    px: 1.5,
    '&:hover': {
      borderColor: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
  };

  const [sortOrder, setSortOrder] = useState('desc');
  const [activityTypeFilter, setActivityTypeFilter] = useState([]);
  const [actorFilter, setActorFilter] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const activityTypeOptions = useMemo(() => (
    Object.entries(ACTIVITY_TYPE_CONFIG)
      .filter(([key]) => key !== 'other')
      .map(([key, config]) => ({ value: key, label: config.label }))
  ), []);

  const actorOptions = useMemo(() => {
    const optionsMap = new Map();
    logs.forEach((log) => {
      const value = getActorFilterValue(log);
      if (!optionsMap.has(value)) {
        optionsMap.set(value, getActorFilterLabel(log));
      }
    });

    return Array.from(optionsMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [logs]);

  const filteredAndSortedLogs = useMemo(() => {
    const fromTimestamp = toStartOfDay(fromDate);
    const toTimestamp = toEndOfDay(toDate);

    const filteredLogs = logs.filter((log) => {
      const logType = getActivityType(log);
      if (activityTypeFilter.length > 0 && !activityTypeFilter.includes(logType)) {
        return false;
      }

      if (actorFilter.length > 0 && !actorFilter.includes(getActorFilterValue(log))) {
        return false;
      }

      const createdAt = new Date(log.created_at).getTime();
      if (Number.isNaN(createdAt)) return false;
      if (fromTimestamp != null && createdAt < fromTimestamp) return false;
      if (toTimestamp != null && createdAt > toTimestamp) return false;
      return true;
    });

    return filteredLogs.sort((a, b) => {
      const first = new Date(a.created_at).getTime();
      const second = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? second - first : first - second;
    });
  }, [logs, sortOrder, activityTypeFilter, actorFilter, fromDate, toDate]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {loadingLogs && logs.length === 0 ? (
        <Box sx={{ p: 0 }}>
          <Skeleton variant="text" width={220} height={30} />
          <Skeleton variant="rectangular" sx={{ mt: 1, borderRadius: 1 }} height={90} />
          <Skeleton variant="rectangular" sx={{ mt: 1, borderRadius: 1 }} height={90} />
        </Box>
      ) : logs.length === 0 && error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
              {error}
            </Alert>
          )}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="flex-start"
            useFlexGap
            sx={{ mb: 2, flexWrap: 'wrap', flexShrink: 0, pt: 0.75, overflow: 'visible' }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSortOrder((current) => (current === 'desc' ? 'asc' : 'desc'))}
              startIcon={<SwapVertOutlinedIcon fontSize="small" />}
              sx={{ ...toolbarButtonSx, minWidth: 148 }}
            >
              {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            </Button>

            <TextField
              size="small"
              select
              label="Activity type"
              value={activityTypeFilter}
              onChange={(event) => setActivityTypeFilter(normalizeMultiSelectValue(event.target.value))}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (!selected?.length) return 'All activity';
                  return selected
                    .map((value) => activityTypeOptions.find((option) => option.value === value)?.label || value)
                    .join(', ');
                },
              }}
              sx={{ minWidth: 180 }}
            >
              {activityTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox size="small" checked={activityTypeFilter.includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              select
              label="User"
              value={actorFilter}
              onChange={(event) => setActorFilter(normalizeMultiSelectValue(event.target.value))}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (!selected?.length) return 'All users';
                  return selected
                    .map((value) => actorOptions.find((option) => option.value === value)?.label || value)
                    .join(', ');
                },
              }}
              sx={{ minWidth: 180 }}
            >
              {actorOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox size="small" checked={actorFilter.includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="From"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              size="small"
              label="To"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setActivityTypeFilter([]);
                setActorFilter([]);
                setFromDate('');
                setToDate('');
              }}
              sx={{
                ...toolbarButtonSx,
                minWidth: 120,
                alignSelf: { xs: 'flex-start', md: 'center' },
              }}
            >
              Clear filters
            </Button>
          </Stack>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {filteredAndSortedLogs.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  {logs.length === 0
                    ? 'No activity recorded for this client yet.'
                    : 'No activity matches the selected filters.'}
                </Typography>
              </Paper>
            ) : (
              filteredAndSortedLogs.map((log) => {
                const activityType = getActivityType(log);
                const activityConfig = ACTIVITY_TYPE_CONFIG[activityType] || ACTIVITY_TYPE_CONFIG.other;
                const ActivityIcon = activityConfig.icon;

                return (
                  <Paper
                    key={log.id}
                    elevation={0}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.background.paper,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.palette.action.focus,
                        boxShadow: theme.shadows[2],
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        icon={<ActivityIcon />}
                        size="small"
                        label={activityConfig.label}
                        color={activityConfig.color}
                        variant={theme.palette.mode === 'dark' ? 'filled' : 'outlined'}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatShortDateTime(log.created_at)}
                      </Typography>
                    </Stack>

                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'pre-line' }}>
                      {formatClientLogEntry(log)}
                    </Typography>

                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                      By {log.actor_name || log.actor_email || 'System'}
                    </Typography>
                  </Paper>
                );
              })
            )}

            {loadingLogs && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ActivityTab;
